import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { datePickerSlotProps } from '@/components/shared/ReportsRevamped/styles';
import { usePaintRegionStatusLabels } from '@/hooks';
import { PaintRegionStatus } from '@/__generated__/graphql';

type RemediationDateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (date: Date) => void;
  paintBlockName?: string;
};

export const RemediationDateModal = ({
  isOpen,
  onClose,
  onConfirm,
  paintBlockName,
}: RemediationDateModalProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const { getPaintBlockLabels } = usePaintRegionStatusLabels();
  const partiallyCompletedLabel = getPaintBlockLabels(
    PaintRegionStatus.PartiallyCompleted
  ).toLowerCase();

  // Reset to current date whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedDate(new Date());
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedDate) {
      onConfirm(selectedDate);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedDate(new Date());
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: 400,
          maxWidth: 500,
        },
      }}
    >
      <DialogTitle>
        Select {partiallyCompletedLabel.charAt(0).toUpperCase() + partiallyCompletedLabel.slice(1)}{' '}
        Date
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            {paintBlockName
              ? `Select the ${partiallyCompletedLabel} date for paint block: ${paintBlockName}`
              : `Select the ${partiallyCompletedLabel} date for this paint block`}
          </Typography>
          <DatePicker
            label={`${
              partiallyCompletedLabel.charAt(0).toUpperCase() + partiallyCompletedLabel.slice(1)
            } Date`}
            value={selectedDate}
            onChange={(newDate) => setSelectedDate(newDate)}
            slotProps={datePickerSlotProps}
            sx={{ width: '100%' }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!selectedDate}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
